import React, { useState, useContext, useEffect } from 'react';
import { Form, Button, Container, Card, Alert, ProgressBar, Tabs, Tab } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { videoLinkKey } from '../utils/videoLink';

/**
 * UploadPage component - allows users to upload karaoke videos
 * Supports both file uploads and video downloads from URLs
 */
const UploadPage = () => {
  const { user } = useContext(AuthContext);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validated, setValidated] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [videoUrl, setVideoUrl] = useState('');
  const [existingVideos, setExistingVideos] = useState([]);
  const [savedVideo, setSavedVideo] = useState(null);
  const duplicate = existingVideos.find(video => videoLinkKey(video.originalUrl || video.path) === videoLinkKey(videoUrl) && videoLinkKey(videoUrl));
  useEffect(() => {
    const controller = new AbortController();
    axios.get('/api/videos', { signal: controller.signal }).then(({ data }) => setExistingVideos(data)).catch(() => {});
    return () => controller.abort();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    if (!file) {
      setError('Please select a video file');
      return;
    }
    
    setValidated(true);
    setUploading(true);
    setError('');
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('title', title);
    formData.append('video', file);
    
    try {
      // Проверяем авторизацию пользователя
      if (!user) {
        setError('Authentication required. Please log in again.');
        setUploading(false);
        return;
      }
      
      // Получаем токен из объекта пользователя
      const token = user.token;
      
      await axios.post('/api/videos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });
      
      setSuccess(true);
      setTitle('');
      setFile(null);
      setValidated(false);
      
      // Reset form
      document.getElementById('upload-form').reset();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };
  
  // Handle URL add (no download)
  const handleUrlDownload = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    if (!videoUrl) {
      setError('Please enter a video URL');
      return;
    }
    
    setValidated(true);
    setUploading(true);
    setError('');
    setUploadProgress(0);
    
    try {
      // Проверяем авторизацию пользователя
      if (!user) {
        setError('Authentication required. Please log in again.');
        setUploading(false);
        return;
      }
      
      // Получаем токен из объекта пользователя
      const token = user.token;
      
      const { data } = await axios.post('/api/videos/create-by-url', 
        { url: videoUrl, title }, 
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setSuccess(true);
      setSavedVideo(data);
      setExistingVideos(previous => [...previous, data]);
      setTitle('');
      setVideoUrl('');
      setValidated(false);
      
      // Reset form
      document.getElementById('url-form').reset();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add video by URL');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container className="py-4">
      <Link to="/" className="btn btn-light mb-4">
        Go Back
      </Link>
      
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h2 className="mb-0">Add Karaoke Video</h2>
        </Card.Header>
        
        <Card.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              setError('');
              setSuccess(false);
              setValidated(false);
            }}
            className="mb-4"
          >
            <Tab eventKey="upload" title="Upload File">
            </Tab>
            <Tab eventKey="url" title="Add by URL">
            </Tab>
          </Tabs>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && (
            <Alert variant="success">
              Video {activeTab === 'upload' ? 'uploaded' : 'added'} successfully! {activeTab === 'url' && savedVideo && <Link to={`/video/${savedVideo.id}`}>Open added video</Link>} <Link to="/">View all videos</Link>
            </Alert>
          )}
          
          {activeTab === 'upload' ? (
            <Form id="upload-form" noValidate validated={validated} onSubmit={handleFileUpload}>
              <Form.Group className="mb-3" controlId="title">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter video title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  disabled={uploading}
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a title.
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="video">
                <Form.Label>Video File</Form.Label>
                <Form.Control
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  required
                  disabled={uploading}
                />
                <Form.Control.Feedback type="invalid">
                  Please select a video file.
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Supported formats: MP4, WebM, MOV (max 100MB)
                </Form.Text>
              </Form.Group>
              
              {uploading && (
                <div className="mb-3">
                  <p>Uploading: {uploadProgress}%</p>
                  <ProgressBar 
                    now={uploadProgress} 
                    label={`${uploadProgress}%`} 
                    variant="info" 
                    animated 
                  />
                </div>
              )}
              
              <Button
                variant="primary"
                type="submit"
                disabled={uploading}
                className="mt-3"
              >
                {uploading ? 'Uploading...' : 'Upload Video'}
              </Button>
            </Form>
          ) : (
            <Form id="url-form" noValidate validated={validated} onSubmit={handleUrlDownload}>
              <Form.Group className="mb-3" controlId="videoUrl">
                <Form.Label>Video URL</Form.Label>
                <Form.Control
                  type="url"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  required
                  disabled={uploading}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid URL.
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Enter the URL of the video you want to play directly (YouTube, direct MP4/WebM, etc.).
                </Form.Text>
              </Form.Group>

              {duplicate && <Alert variant="warning">This video link is already used by <Link to={`/video/${duplicate.id}`}>{duplicate.title}</Link>. Check that you copied the intended video URL before adding it again.</Alert>}

              <Form.Group className="mb-3" controlId="videoTitle">
                <Form.Label>Video Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter video title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  disabled={uploading}
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a title for the video.
                </Form.Control.Feedback>
              </Form.Group>

              {uploading && (
                <div className="mb-3">
                  <ProgressBar animated now={100} label="Downloading..." />
                </div>
              )}

              <div className="d-grid">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={uploading}
                  className="py-2"
                >
                  {uploading ? 'Saving...' : 'Add URL'}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

// Export the component as default
export default UploadPage;
